import { useState } from "react";
import { Instagram, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { CONTACT, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", service: "", message: "" };

const inputCls =
  "w-full border-b border-royal-line bg-transparent py-3 text-base text-foreground outline-none transition-colors duration-300 placeholder:text-muted-foreground/60 focus:border-royal";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("fail");
      toast.success("Pedido enviado. Vamos responder em breve.");
      setForm(EMPTY);
    } catch {
      toast.error("Não foi possível enviar. Tenta novamente ou escreve-nos por email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="bg-white py-28 md:py-40" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl gap-20 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Contacto</Overline>
            <h2 className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-royal-deep md:text-6xl">
              Vamos <em className="text-royal">conversar.</em>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
              Conta-nos a tua ideia. Respondemos pessoalmente, com atenção, rapidez e uma
              proposta à medida da tua marca.
            </p>
          </Reveal>

          <Reveal delay={0.2} className="mt-12 space-y-5">
            <a
              href={`mailto:${CONTACT.email}`}
              className="group flex items-center gap-4 text-foreground/80 transition-colors duration-300 hover:text-royal"
              data-testid="contact-email-link"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-royal-line text-royal transition-colors duration-300 group-hover:bg-royal group-hover:text-white">
                <Mail className="h-4 w-4" />
              </span>
              <span className="text-base font-semibold">{CONTACT.email}</span>
            </a>
            <a
              href={CONTACT.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 text-foreground/80 transition-colors duration-300 hover:text-royal"
              data-testid="contact-instagram-link"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-royal-line text-royal transition-colors duration-300 group-hover:bg-royal group-hover:text-white">
                <Instagram className="h-4 w-4" />
              </span>
              <span className="text-base font-semibold">{CONTACT.instagram}</span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="text-xs font-bold uppercase tracking-[0.2em] text-royal">
                    Nome
                  </label>
                  <input
                    id="cf-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="O teu nome"
                    className={inputCls}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="text-xs font-bold uppercase tracking-[0.2em] text-royal">
                    Email
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nome@email.com"
                    className={inputCls}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-company" className="text-xs font-bold uppercase tracking-[0.2em] text-royal">
                    Empresa
                  </label>
                  <input
                    id="cf-company"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="A tua marca ou empresa"
                    className={inputCls}
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label htmlFor="cf-service" className="text-xs font-bold uppercase tracking-[0.2em] text-royal">
                    Serviço
                  </label>
                  <select
                    id="cf-service"
                    required
                    value={form.service}
                    onChange={set("service")}
                    className={`${inputCls} cursor-pointer`}
                    data-testid="contact-service-select"
                  >
                    <option value="" disabled>
                      Escolhe um serviço
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s.n} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="cf-message" className="text-xs font-bold uppercase tracking-[0.2em] text-royal">
                  Mensagem
                </label>
                <textarea
                  id="cf-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Conta-nos sobre o teu projeto..."
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="group flex items-center gap-3 rounded-full bg-royal px-8 py-4 text-sm font-bold text-white transition-all duration-500 hover:bg-black/10 hover:shadow-[0_12px_40px_rgba(42,80,170,0.4)] disabled:opacity-60"
                data-testid="contact-submit-button"
              >
                {sending ? (
                  <>
                    A enviar...
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Enviar pedido
                    <Send className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
